import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/contexts/AuthContext';
import { playZippieSound } from '@/lib/zippieSound';
import ReportQuestionButton from '@/components/ReportQuestionButton';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import Confetti from 'react-confetti';
import {
  ArrowLeft, ArrowRight, BookOpen, Calculator, CheckCircle, XCircle,
  Star, Trophy, Play, Volume2, Loader2, Target, Sparkles, Gift,
  ChevronRight, Award, Brain, Pencil, FlaskConical, Lightbulb,
  TrendingUp, TrendingDown, Minus, RefreshCw, GraduationCap, Shield,
  AlertTriangle, Lock, Globe
} from 'lucide-react';
import VoiceSelector, { VOICE_OPTIONS } from '@/components/VoiceSelector';
import { useElevenLabsTTS } from '@/hooks/useElevenLabsTTS';
import LikeButton from '@/components/LikeButton';
import ConceptIllustration from '@/components/ConceptIllustration';

const AdaptiveAICurriculum = () => {
  const { childId, subject } = useParams();
  const navigate = useNavigate();
  const { api } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [child, setChild] = useState(null);
  const [lesson, setLesson] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [typedAnswer, setTypedAnswer] = useState('');
  const [feedback, setFeedback] = useState(null);
  const [sessionStats, setSessionStats] = useState(null);
  const [phase, setPhase] = useState('lesson'); // safety-required, lesson, teaching, practice, results
  const [showConfetti, setShowConfetti] = useState(false);
  const [curriculumInfo, setCurriculumInfo] = useState(null);
  const [levelChange, setLevelChange] = useState(null);
  const [digitalSafetyCompleted, setDigitalSafetyCompleted] = useState(false);
  const [currentConceptIndex, setCurrentConceptIndex] = useState(0);
  const [preK, setPreK] = useState(false);
  
  // Voice settings for TTS (ElevenLabs only)
  const [selectedVoice, setSelectedVoice] = useState('shimmer');
  const [speechSpeed, setSpeechSpeed] = useState(0.95);
  const { speak: speakText, stop: stopSpeak, isSpeaking } = useElevenLabsTTS({ voice: selectedVoice, speed: speechSpeed });

  const subjectConfig = {
    reading: { 
      name: 'Reading', 
      icon: BookOpen, 
      color: '#10B981', 
      bgGradient: 'from-emerald-400 to-teal-500',
      curriculum: 'EL Education'
    },
    math: { 
      name: 'Math', 
      icon: Calculator, 
      color: '#EF4444', 
      bgGradient: 'from-red-400 to-orange-500',
      curriculum: 'Envision Math + Kiddom CCSS'
    },
    writing: { 
      name: 'Writing', 
      icon: Pencil, 
      color: '#06B6D4', 
      bgGradient: 'from-cyan-400 to-blue-500',
      curriculum: 'EL Education'
    },
    science: { 
      name: 'Science', 
      icon: FlaskConical, 
      color: '#8B5CF6', 
      bgGradient: 'from-purple-400 to-indigo-500',
      curriculum: 'NGSS Science'
    },
    history: {
      name: 'History',
      icon: GraduationCap,
      color: '#D97706',
      bgGradient: 'from-amber-500 to-orange-600',
      curriculum: 'C3 Social Studies'
    },
    arts: {
      name: 'Arts',
      icon: Sparkles,
      color: '#EC4899',
      bgGradient: 'from-pink-400 to-rose-500',
      curriculum: 'National Core Arts'
    },
  };

  const config = subjectConfig[subject] || subjectConfig.reading;

  useEffect(() => {
    fetchInitialData();
  }, [childId, subject]);

  const fetchInitialData = async () => {
    try {
      const [childRes, curriculumRes, safetyRes] = await Promise.all([
        api.get(`/children/${childId}`),
        api.get(`/adaptive/curriculum-overview/${subject}`),
        api.get(`/digital-safety/status/${childId}`)
      ]);
      
      setChild(childRes.data);
      setCurriculumInfo(curriculumRes.data);
      setDigitalSafetyCompleted(safetyRes.data.digital_safety_completed);
      
      // Compute pre-K locally so we can gate the concept/lesson UI before Start Practice
      const bd = childRes.data?.birth_date;
      let localPreK = false;
      if (bd) {
        try {
          const dt = new Date(bd);
          const now = new Date();
          const months = (now.getFullYear() - dt.getFullYear()) * 12 + (now.getMonth() - dt.getMonth());
          localPreK = months < 72;
        } catch {}
      }
      if (!localPreK) {
        const ag = String(childRes.data?.age_group || '').toLowerCase();
        localPreK = ['infant','toddler','preschool'].includes(ag);
      }
      setPreK(localPreK);
      
      // Check if digital safety is completed
      if (!safetyRes.data.digital_safety_completed) {
        setPhase('safety-required');
        setLoading(false);
        return;
      }
      
      // Auto-generate first lesson
      await generateLesson();
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load curriculum');
    } finally {
      setLoading(false);
    }
  };

  const generateLesson = async (skillFocus = null) => {
    setGenerating(true);
    try {
      const response = await api.post(`/adaptive/ai-lesson/${childId}/${subject}`, {
        skill_focus: skillFocus,
        force_regenerate: skillFocus !== null
      });
      
      setLesson(response.data.lesson);
      
      if (response.data.source === 'ai_generated') {
        toast.success('AI generated a personalized lesson!', {
          icon: <Sparkles className="w-4 h-4 text-amber-500" />
        });
      }
    } catch (error) {
      console.error('Failed to generate lesson:', error);
      toast.error('Failed to generate lesson');
    } finally {
      setGenerating(false);
    }
  };

  // Build a friendly, kindergarten-appropriate narration of the whole lesson intro
  const buildLessonNarration = (l) => {
    if (!l) return '';
    const parts = [];
    if (l.title) parts.push(`Today we're going to learn about ${l.title}.`);
    if (l.instruction?.introduction) parts.push(l.instruction.introduction);
    const objectives = l.objectives || [];
    if (objectives.length) {
      parts.push("Here's what you'll learn.");
      objectives.slice(0, 3).forEach((o) => parts.push(o));
    }
    const tips = l.instruction?.tips || [];
    if (tips.length) {
      parts.push("A helpful tip.");
      parts.push(tips[0]);
    }
    parts.push("When you're ready, tap the picture to start.");
    return parts.filter(Boolean).join(' ');
  };

  // Auto-narrate the lesson on first render for Pre-K children
  useEffect(() => {
    if (!preK || !lesson || phase !== 'lesson') return;
    const narration = buildLessonNarration(lesson);
    if (narration) {
      // small delay so the UI paints before the audio starts
      const t = setTimeout(() => speakText(narration), 500);
      return () => clearTimeout(t);
    }
  }, [preK, lesson, phase]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-narrate each teaching concept card so children (not just Pre-K) can
  // hear the concept, explanation, example, tip, and the "Do you understand?" prompt.
  // Respects the child's `read_whole_page` narrator-first toggle
  // (default: ON for kids under 8, unless the parent has flipped it off).
  useEffect(() => {
    if (phase !== 'teaching') return;
    const concept = lesson?.instruction?.key_concepts?.[currentConceptIndex];
    if (!concept) return;
    // Narrator-first gate: honour explicit false; otherwise fall back to age-based default.
    const explicitToggle = child?.read_whole_page;
    const ageYears = (() => {
      try {
        if (!child?.birth_date) return null;
        const bd = new Date(child.birth_date);
        return (Date.now() - bd.getTime()) / (365.25 * 24 * 3600 * 1000);
      } catch { return null; }
    })();
    const shouldAutoRead = explicitToggle === false ? false
      : explicitToggle === true ? true
      : (ageYears == null || ageYears < 8);
    if (!shouldAutoRead) return;
    const example = lesson?.instruction?.examples?.[currentConceptIndex];
    const tip = lesson?.instruction?.tips?.[currentConceptIndex];
    const understanding = 'Do you understand this concept? Take your time. Click Next when you\'re ready to continue.';
    const text = [
      concept.concept,
      concept.explanation,
      example ? `Here's an example: ${example}` : '',
      tip ? `Helpful tip: ${tip}` : '',
      understanding,
    ].filter(Boolean).join(' ');
    if (text) {
      const t = setTimeout(() => speakText(text), 400);
      return () => clearTimeout(t);
    }
  }, [phase, currentConceptIndex, lesson, child]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-narrate answer feedback for Pre-K children (right / try again + explanation)
  useEffect(() => {
    if (!preK || !feedback) return;
    const parts = [];
    parts.push(feedback.correct ? 'Yay, that is right!' : 'Nice try — let me help.');
    if (feedback.correct_answer && !feedback.correct) {
      parts.push(`The answer is ${feedback.correct_answer}.`);
    }
    if (feedback.explanation) parts.push(feedback.explanation);
    const narration = parts.join(' ');
    const t = setTimeout(() => speakText(narration), 200);
    return () => clearTimeout(t);
  }, [preK, feedback]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-narrate results screen
  useEffect(() => {
    if (!preK || phase !== 'results') return;
    const correct = sessionStats?.questions_correct ?? 0;
    const total = sessionStats?.questions_answered ?? questions.length ?? 0;
    const line = total ? `You got ${correct} out of ${total} right. Great work!` : 'You did it! Great work!';
    const t = setTimeout(() => speakText(line), 400);
    return () => clearTimeout(t);
  }, [preK, phase, questions, sessionStats]); // eslint-disable-line react-hooks/exhaustive-deps

  // Transition to teaching phase - students MUST learn concepts before practice
  const startTeaching = () => {
    setCurrentConceptIndex(0);
    setPhase('teaching');
  };

  // Navigate through concepts in teaching phase
  const nextConcept = () => {
    const concepts = lesson?.instruction?.key_concepts || [];
    if (currentConceptIndex < concepts.length - 1) {
      setCurrentConceptIndex(prev => prev + 1);
    } else {
      // All concepts learned, now can start practice
      startPractice();
    }
  };

  const prevConcept = () => {
    if (currentConceptIndex > 0) {
      setCurrentConceptIndex(prev => prev - 1);
    }
  };

  const startPractice = async () => {
    setGenerating(true);
    try {
      const skillFocus = lesson?.skill_focus || curriculumInfo?.levels?.[0]?.focus_areas?.[0] || 'general';
      
      const response = await api.post(`/adaptive/ai-questions/${childId}/${subject}`, {
        skill_focus: skillFocus,
        count: 20,
        difficulty: 'auto'
      });
      
      setQuestions(response.data.questions || []);
      setPreK(Boolean(response.data.pre_k));
      setCurrentQuestion(0);
      setSelectedAnswer(null);
      setFeedback(null);
      setPhase('practice');
      
      // Auto-speak first pre-K question (audio-first)
      if (response.data.pre_k && response.data.questions?.[0]) {
        const q0 = response.data.questions[0];
        setTimeout(() => speakText(q0.audio_prompt || q0.question), 300);
      }
      toast.success(`${response.data.difficulty} difficulty questions generated!`, {
        icon: <Brain className="w-4 h-4 text-purple-500" />
      });
    } catch (error) {
      console.error('Failed to generate questions:', error);
      // Check if error is due to digital safety
      if (error.response?.status === 403) {
        toast.error('Please complete Digital Safety training first');
        setPhase('safety-required');
      } else {
        const detail = error.response?.data?.detail || error.message || 'Unknown error';
        toast.error(`Failed to start practice: ${detail}`);
      }
    } finally {
      setGenerating(false);
    }
  };

  const submitAnswer = async () => {
    const currentQ = questions[currentQuestion];
    if (!currentQ) return;
    
    // Get the answer based on question type
    const answerValue = currentQ.type === 'input' || currentQ.type === 'short_answer' || currentQ.type === 'fill_in_blank'
      ? typedAnswer
      : selectedAnswer;
    
    if (!answerValue || answerValue.trim() === '') {
      toast.error('Please provide an answer');
      return;
    }
    
    try {
      const response = await api.post(`/adaptive/ai-answer/${childId}/${subject}`, {
        question_id: currentQ.id,
        answer: answerValue,
        time_spent_seconds: 30 // Simplified timing
      });
      
      setFeedback(response.data);
      setSessionStats(response.data.session_stats);

      // Celebrate correct answers with the Zippie chime + voice.
      if (response.data.correct) {
        try { playZippieSound(); } catch { /* no-op */ }
      }

      if (response.data.level_change) {
        setLevelChange(response.data.level_change);
        if (response.data.level_change === 'up') {
          setShowConfetti(true);
          toast.success('Level Up! You\'re advancing to the next level!', {
            icon: <TrendingUp className="w-4 h-4 text-green-500" />
          });
          setTimeout(() => setShowConfetti(false), 5000);
        }
      }
    } catch (error) {
      console.error('Failed to submit answer:', error);
      toast.error('Failed to submit answer');
    }
  };

  const nextQuestion = () => {
    if (currentQuestion < questions.length - 1) {
      const nextIdx = currentQuestion + 1;
      setCurrentQuestion(nextIdx);
      setSelectedAnswer(null);
      setTypedAnswer('');
      setFeedback(null);
      // Auto-speak next question in Pre-K mode
      if (preK) {
        const q = questions[nextIdx];
        if (q) setTimeout(() => speakText(q.audio_prompt || q.question), 250);
      }
    } else {
      completeSession();
    }
  };

  const completeSession = async () => {
    try {
      const response = await api.post(`/adaptive/ai-session/complete/${childId}/${subject}`);
      setSessionStats(response.data.summary);
      setPhase('results');
      // Fetch the retry queue so we can offer "Practice missed questions"
      loadRetryQueue();

      if (response.data.summary.mastery_score >= 80) {
        setShowConfetti(true);
        setTimeout(() => setShowConfetti(false), 5000);
      }
    } catch (error) {
      console.error('Failed to complete session:', error);
      // Still show results with current stats
      setPhase('results');
      loadRetryQueue();
    }
  };

  const [retryQueue, setRetryQueue] = useState([]);
  const [loadingRetry, setLoadingRetry] = useState(false);
  const [retryMode, setRetryMode] = useState(false); // true when replaying missed questions

  const loadRetryQueue = useCallback(async () => {
    setLoadingRetry(true);
    try {
      const res = await api.get(`/adaptive/retry-queue/${childId}/${subject}`);
      setRetryQueue(res.data.questions || []);
    } catch (err) {
      setRetryQueue([]);
    } finally {
      setLoadingRetry(false);
    }
  }, [api, childId, subject]);

  const startRetryPractice = () => {
    if (!retryQueue.length) return;
    setPhase('practice');
    setQuestions(retryQueue);
    setCurrentQuestion(0);
    setSelectedAnswer(null);
    setTypedAnswer('');
    setFeedback(null);
    setSessionStats(null);
    setRetryMode(true);
  };

  const restartLesson = () => {
    setPhase('lesson');
    setQuestions([]);
    setCurrentQuestion(0);
    setSelectedAnswer(null);
    setFeedback(null);
    setSessionStats(null);
    setLevelChange(null);
    generateLesson();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-emerald-500 mx-auto mb-4" />
          <p className="text-slate-600">Loading adaptive curriculum...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {showConfetti && <Confetti recycle={false} numberOfPieces={300} />}
      
      {/* Header */}
      <header className={`bg-gradient-to-r ${config.bgGradient} text-white py-4 px-4 sticky top-0 z-40`}>
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Link to={`/learn/${childId}`} data-testid="back-to-learning">
            <Button variant="ghost" size="icon" className="text-white hover:bg-white/20">
              <ArrowLeft className="w-5 h-5" />
            </Button>
          </Link>
          
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center`}>
              <config.icon className="w-6 h-6" />
            </div>
            <div>
              <h1 className="font-bold text-lg">{config.name}</h1>
              <p className="text-xs text-white/80">{config.curriculum} Curriculum</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Badge className="bg-white/20 text-white border-0">
              Level {child?.subject_levels?.[subject] || 1}
            </Badge>
            <div className="flex items-center gap-1 bg-white/20 px-2 py-1 rounded-full text-sm">
              <Star className="w-4 h-4 text-amber-300 fill-amber-300" />
              <span>{child?.total_xp || 0}</span>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6">
        <AnimatePresence mode="wait">
          {/* DIGITAL SAFETY REQUIRED PHASE */}
          {phase === 'safety-required' && (
            <motion.div
              key="safety-required"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="space-y-6"
            >
              <Card className="border-0 shadow-xl overflow-hidden bg-gradient-to-br from-slate-800 to-slate-900">
                <div className="bg-gradient-to-r from-cyan-600 to-blue-600 p-8 text-center text-white">
                  <Shield className="w-20 h-20 mx-auto mb-4" />
                  <h2 className="text-2xl font-bold mb-2">Digital Safety Training Required</h2>
                  <p className="text-cyan-100">Before starting your learning journey, please complete the Digital Safety lesson</p>
                </div>
                
                <CardContent className="p-6 space-y-4">
                  <div className="bg-amber-900/30 border border-amber-700/50 rounded-xl p-4">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="w-5 h-5 text-amber-400 mt-0.5" />
                      <div>
                        <h3 className="font-semibold text-amber-300">Why is this important?</h3>
                        <p className="text-amber-100 text-sm mt-1">
                          Learning how to stay safe online is just as important as learning to read and do math. 
                          This short lesson will teach you how to protect yourself while using apps and the internet.
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="space-y-2 text-slate-300">
                    <p className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-emerald-400" />
                      Learn important safety rules
                    </p>
                    <p className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-emerald-400" />
                      Takes only 5-10 minutes
                    </p>
                    <p className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-emerald-400" />
                      Earn 50 bonus XP!
                    </p>
                  </div>
                  
                  <Button
                    onClick={() => navigate(`/digital-safety/${childId}`)}
                    className="w-full bg-gradient-to-r from-cyan-600 to-blue-600"
                    data-testid="start-safety-lesson-btn"
                  >
                    <Shield className="w-4 h-4 mr-2" />
                    Start Digital Safety Lesson
                  </Button>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* LESSON PHASE */}
          {phase === 'lesson' && (
            <motion.div
              key="lesson"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="space-y-6"
            >
              {preK && (
                <Card className="border-0 shadow-md bg-gradient-to-r from-amber-400 via-orange-400 to-rose-400 text-white" data-testid="prek-lesson-banner">
                  <CardContent className="p-4 flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <Sparkles className="w-6 h-6" />
                      <div>
                        <p className="font-bold">Little Learner Mode</p>
                        <p className="text-xs opacity-90">Tap the pictures — we'll read everything to you.</p>
                      </div>
                    </div>
                    <Button
                      size="sm"
                      className="bg-white/25 hover:bg-white/35 text-white border-0"
                      onClick={() => speakText(curriculumInfo?.curriculum_description || lesson?.introduction || 'Ready to play?')}
                      data-testid="prek-lesson-hear-btn"
                    >
                      <Volume2 className="w-4 h-4 mr-1" /> Hear it
                    </Button>
                  </CardContent>
                </Card>
              )}
              {/* Curriculum Info Card */}
              {curriculumInfo && !preK && (
                <Card className="border-0 shadow-lg bg-gradient-to-br from-white to-slate-50">
                  <CardHeader className="pb-2">
                    <div className="flex items-center gap-2">
                      <GraduationCap className="w-5 h-5 text-indigo-500" />
                      <CardTitle className="text-lg">{curriculumInfo.curriculum_name}</CardTitle>
                    </div>
                    <CardDescription>{curriculumInfo.curriculum_description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-2">
                      {curriculumInfo.levels?.find(l => l.level === (child?.subject_levels?.[subject] || 1))?.focus_areas?.map((focus, idx) => (
                        <Badge key={idx} variant="secondary" className="text-xs">
                          {focus.replace(/_/g, ' ')}
                        </Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* AI-Generated Lesson Card */}
              <Card className="border-0 shadow-xl overflow-hidden" data-testid="ai-lesson-card">
                {generating ? (
                  <div className="p-12 text-center">
                    <Sparkles className="w-12 h-12 text-amber-500 mx-auto mb-4 animate-pulse" />
                    <p className="text-slate-600 font-medium">AI is generating your personalized lesson...</p>
                  </div>
                ) : lesson ? (
                  <>
                    <div className={`bg-gradient-to-r ${config.bgGradient} p-6 text-white`}>
                      <div className="flex items-center gap-2 mb-2">
                        <Sparkles className="w-5 h-5 text-amber-300" />
                        <span className="text-sm font-medium text-white/80">AI-Powered Lesson</span>
                      </div>
                      <h2 className="text-2xl font-bold">{lesson.title}</h2>
                      <p className="text-white/90 mt-1">{lesson.description}</p>
                      <div className="flex gap-2 mt-3">
                        <Badge className="bg-white/20 text-white border-0">
                          {lesson.grade_range}
                        </Badge>
                        <Badge className="bg-white/20 text-white border-0">
                          {lesson.skill_focus?.replace(/_/g, ' ')}
                        </Badge>
                      </div>
                    </div>
                    
                    <CardContent className="p-6 space-y-6">
                      {/* Learning Objectives */}
                      <div>
                        <h3 className="font-semibold text-slate-800 flex items-center gap-2 mb-3">
                          <Target className="w-5 h-5 text-emerald-500" />
                          What You'll Learn
                        </h3>
                        <ul className="space-y-2">
                          {lesson.objectives?.map((obj, idx) => (
                            <li key={idx} className="flex items-start gap-2 text-slate-600">
                              <CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                              <span>{obj}</span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      {/* Introduction */}
                      <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-100">
                        <p className="text-slate-700 leading-relaxed">
                          {lesson.instruction?.introduction}
                        </p>
                      </div>

                      {/* Key Concepts */}
                      <div>
                        <h3 className="font-semibold text-slate-800 flex items-center gap-2 mb-3">
                          <Lightbulb className="w-5 h-5 text-amber-500" />
                          Key Concepts
                        </h3>
                        <div className="grid gap-3">
                          {lesson.instruction?.key_concepts?.map((concept, idx) => (
                            <div key={idx} className="bg-slate-50 rounded-lg p-4 border border-slate-100">
                              <h4 className="font-medium text-slate-800 mb-1">{concept.concept}</h4>
                              <p className="text-slate-600 text-sm">{concept.explanation}</p>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Examples */}
                      {lesson.instruction?.examples?.length > 0 && (
                        <div>
                          <h3 className="font-semibold text-slate-800 mb-3">Examples</h3>
                          <div className="space-y-2">
                            {lesson.instruction.examples.map((ex, idx) => (
                              <div key={idx} className="flex items-center gap-2 text-slate-600 bg-blue-50 p-3 rounded-lg">
                                <Star className="w-4 h-4 text-blue-500 flex-shrink-0" />
                                <span>{ex}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Tips */}
                      {lesson.instruction?.tips?.length > 0 && (
                        <div className="bg-amber-50 rounded-xl p-4 border border-amber-100">
                          <h3 className="font-semibold text-amber-800 flex items-center gap-2 mb-2">
                            <Lightbulb className="w-4 h-4" />
                            Learning Tips
                          </h3>
                          <ul className="space-y-1">
                            {lesson.instruction.tips.map((tip, idx) => (
                              <li key={idx} className="text-amber-700 text-sm flex items-start gap-2">
                                <span>•</span>
                                <span>{tip}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </CardContent>
                  </>
                ) : (
                  <div className="p-12 text-center">
                    <p className="text-slate-500">No lesson available</p>
                  </div>
                )}
              </Card>

              {/* Action Buttons */}
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => generateLesson(null)}
                  disabled={generating}
                  className="flex-1"
                  data-testid="regenerate-lesson-btn"
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${generating ? 'animate-spin' : ''}`} />
                  New Lesson
                </Button>
                <Button
                  onClick={startTeaching}
                  disabled={generating || !lesson}
                  className={`flex-1 bg-gradient-to-r ${config.bgGradient} hover:opacity-90`}
                  data-testid="start-learning-btn"
                >
                  <Play className="w-4 h-4 mr-2" />
                  Start Learning
                </Button>
              </div>
            </motion.div>
          )}

          {/* TEACHING PHASE - Learn concepts step by step before practice */}
          {phase === 'teaching' && lesson?.instruction?.key_concepts && (
            <motion.div
              key="teaching"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="space-y-6"
            >
              {/* Progress */}
              <Card className="border-0 shadow-md">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-slate-500">
                      Learning Concept {currentConceptIndex + 1} of {lesson.instruction.key_concepts.length}
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => speakText(lesson.instruction.key_concepts[currentConceptIndex]?.explanation || '')}
                      className="text-slate-500 hover:text-slate-700"
                      disabled={isSpeaking}
                    >
                      {isSpeaking ? (
                        <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                      ) : (
                        <Volume2 className="w-4 h-4 mr-1" />
                      )}
                      {isSpeaking ? 'Reading...' : 'Read Aloud'}
                    </Button>
                  </div>
                  <Progress 
                    value={((currentConceptIndex + 1) / lesson.instruction.key_concepts.length) * 100} 
                    className="h-2"
                  />
                  {/* Voice Settings */}
                  <div className="mt-3 pt-3 border-t border-slate-100">
                    <VoiceSelector
                      voice={selectedVoice}
                      onVoiceChange={setSelectedVoice}
                      speed={speechSpeed}
                      onSpeedChange={setSpeechSpeed}
                      compact={true}
                      theme="light"
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Current Concept Card */}
              <Card className="border-0 shadow-xl overflow-hidden" data-testid="teaching-concept-card">
                <div className={`bg-gradient-to-r ${config.bgGradient} p-6 text-white`}>
                  <Badge className="bg-white/20 text-white border-0 mb-2">
                    Concept {currentConceptIndex + 1}
                  </Badge>
                  <h2 className="text-2xl font-bold">
                    {lesson.instruction.key_concepts[currentConceptIndex]?.concept}
                  </h2>
                </div>
                
                <CardContent className="p-6 space-y-6">
                  {/* AI-generated concept illustration */}
                  <ConceptIllustration
                    concept={lesson.instruction.key_concepts[currentConceptIndex]?.concept || ''}
                    explanation={lesson.instruction.key_concepts[currentConceptIndex]?.explanation || ''}
                    subject={subject || 'general'}
                  />

                  {/* Main Explanation */}
                  <div className="bg-slate-50 rounded-xl p-6 border border-slate-100">
                    <p className="text-slate-700 text-lg leading-relaxed">
                      {lesson.instruction.key_concepts[currentConceptIndex]?.explanation}
                    </p>
                  </div>

                  {/* Visual Example if available */}
                  {lesson.instruction.examples?.[currentConceptIndex] && (
                    <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
                      <h3 className="font-semibold text-blue-800 flex items-center gap-2 mb-2">
                        <Star className="w-4 h-4" />
                        Example
                      </h3>
                      <p className="text-blue-700">{lesson.instruction.examples[currentConceptIndex]}</p>
                    </div>
                  )}

                  {/* Helpful Tip */}
                  {lesson.instruction.tips?.[currentConceptIndex] && (
                    <div className="bg-amber-50 rounded-xl p-4 border border-amber-100">
                      <h3 className="font-semibold text-amber-800 flex items-center gap-2 mb-2">
                        <Lightbulb className="w-4 h-4" />
                        Helpful Tip
                      </h3>
                      <p className="text-amber-700">{lesson.instruction.tips[currentConceptIndex]}</p>
                    </div>
                  )}

                  {/* Check Understanding prompt */}
                  <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-100 text-center" data-testid="understand-prompt">
                    <CheckCircle className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
                    <p className="text-emerald-700 font-medium">
                      Do you understand this concept?
                    </p>
                    <p className="text-emerald-600 text-sm mt-1">
                      Take your time! Click "Next" when you're ready to continue.
                    </p>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="mt-2 text-emerald-700 hover:text-emerald-800"
                      onClick={() => speakText('Do you understand this concept? Take your time. Click Next when you\'re ready to continue.')}
                      disabled={isSpeaking}
                      data-testid="understand-prompt-tts"
                    >
                      <Volume2 className="w-4 h-4 mr-1" /> Listen
                    </Button>
                  </div>

                  {/* SWD / ELL accessibility supports */}
                  {(child?.has_disability || child?.is_esl_learner) && (
                    <div className="grid gap-3 sm:grid-cols-2" data-testid="accessibility-supports">
                      {child?.has_disability && (
                        <div className="bg-purple-50 border-2 border-purple-200 rounded-xl p-4" data-testid="swd-support-card">
                          <div className="flex items-center gap-2 mb-2">
                            <Shield className="w-5 h-5 text-purple-600" />
                            <span className="font-semibold text-purple-800">Learning Support (SWD)</span>
                          </div>
                          <ul className="text-sm text-purple-700 space-y-1 list-disc list-inside">
                            <li>Take your time — read as slow as you like.</li>
                            <li>Tap <strong>Listen</strong> any time to hear it again.</li>
                            <li>Break big ideas into smaller pieces.</li>
                            {child?.disability_notes && (
                              <li className="italic">Teacher note: {child.disability_notes}</li>
                            )}
                          </ul>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="mt-2 text-purple-700 hover:text-purple-800 px-2"
                            onClick={() => speakText([
                              lesson.instruction.key_concepts[currentConceptIndex]?.concept,
                              lesson.instruction.key_concepts[currentConceptIndex]?.explanation,
                              'Take your time. You can listen as many times as you want.'
                            ].filter(Boolean).join(' '))}
                            disabled={isSpeaking}
                            data-testid="swd-listen-again"
                          >
                            <Volume2 className="w-4 h-4 mr-1" /> Listen again
                          </Button>
                        </div>
                      )}
                      {child?.is_esl_learner && (
                        <div className="bg-sky-50 border-2 border-sky-200 rounded-xl p-4" data-testid="ell-support-card">
                          <div className="flex items-center gap-2 mb-2">
                            <Globe className="w-5 h-5 text-sky-600" />
                            <span className="font-semibold text-sky-800">English Learner Support (ELL)</span>
                          </div>
                          <ul className="text-sm text-sky-700 space-y-1 list-disc list-inside">
                            <li>Words are read slowly and clearly.</li>
                            <li>Look at the picture — it shows the idea.</li>
                            <li>New words are explained in simple English.</li>
                            {child?.native_language && (
                              <li className="italic">Native language on file: {child.native_language}</li>
                            )}
                          </ul>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="mt-2 text-sky-700 hover:text-sky-800 px-2"
                            onClick={() => speakText(lesson.instruction.key_concepts[currentConceptIndex]?.explanation || '', { speed: 0.75 })}
                            disabled={isSpeaking}
                            data-testid="ell-slow-listen"
                          >
                            <Volume2 className="w-4 h-4 mr-1" /> Listen slowly
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Navigation */}
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={prevConcept}
                  disabled={currentConceptIndex === 0}
                  className="flex-1"
                  data-testid="prev-concept-btn"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Previous
                </Button>
                <Button
                  onClick={nextConcept}
                  disabled={generating}
                  className={`flex-1 bg-gradient-to-r ${config.bgGradient}`}
                  data-testid="next-concept-btn"
                >
                  {currentConceptIndex < lesson.instruction.key_concepts.length - 1 ? (
                    <>
                      Next Concept
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </>
                  ) : generating ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Preparing Questions...
                    </>
                  ) : (
                    <>
                      Start Practice
                      <Brain className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
              </div>
            </motion.div>
          )}

          {/* PRACTICE PHASE */}
          {phase === 'practice' && questions.length > 0 && (
            <motion.div
              key="practice"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="space-y-6"
            >
              {/* Progress Header */}
              <Card className="border-0 shadow-md">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-slate-500">
                      Question {currentQuestion + 1} of {questions.length}
                    </span>
                    {sessionStats && (
                      <div className="flex items-center gap-4 text-sm">
                        <span className="text-emerald-600">
                          {sessionStats.questions_correct}/{sessionStats.questions_answered} correct
                        </span>
                        <Badge variant={sessionStats.mastery_score >= 80 ? 'default' : 'secondary'}>
                          {sessionStats.mastery_score}% Mastery
                        </Badge>
                      </div>
                    )}
                  </div>
                  <Progress 
                    value={(currentQuestion / questions.length) * 100} 
                    className="h-2"
                  />
                </CardContent>
              </Card>

              {/* Question Card */}
              <Card className="border-0 shadow-xl" data-testid="question-card">
                <CardContent className="p-6">
                  {preK && (() => {
                    const q = questions[currentQuestion] || {};
                    const hasVisual = !!q.visual;
                    return (
                      <div className="mb-4 p-3 rounded-xl bg-amber-100 border border-amber-300 flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2">
                          <Sparkles className="w-5 h-5 text-amber-600" />
                          <span className="text-sm font-semibold text-amber-800">
                            Little Learner mode · {hasVisual ? 'tap the picture!' : 'tap your answer!'}
                          </span>
                        </div>
                        <Button
                          size="sm"
                          variant="secondary"
                          className="bg-amber-500 hover:bg-amber-600 text-white"
                          onClick={() => speakText(q.audio_prompt || q.question || '')}
                          data-testid="prek-hear-again-btn"
                        >
                          <Volume2 className="w-4 h-4 mr-1" />
                          Hear it
                        </Button>
                      </div>
                    );
                  })()}
                  <div className="mb-6">
                    <div className="flex items-start justify-between gap-2 mb-3">
                      <Badge variant="outline">
                        {questions[currentQuestion]?.skill?.replace(/_/g, ' ')} • {questions[currentQuestion]?.difficulty}
                      </Badge>
                      <ReportQuestionButton
                        question={questions[currentQuestion]}
                        subject={subject}
                        level={sessionStats?.level}
                        childId={childId}
                        childName={child?.name}
                        compact
                      />
                    </div>
                    <h2 className={preK ? "text-3xl font-bold text-slate-800 leading-snug" : "text-xl font-semibold text-slate-800"}>
                      {questions[currentQuestion]?.question}
                    </h2>
                    {questions[currentQuestion]?.visual && (
                      <div
                        className="mt-4 p-6 rounded-2xl bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-200 text-center leading-none cursor-pointer select-none"
                        style={{ fontSize: 'clamp(3rem, 8vw, 5rem)', letterSpacing: '0.08em', wordBreak: 'break-word' }}
                        onClick={() => speakText(questions[currentQuestion]?.audio_prompt || questions[currentQuestion]?.question || '')}
                        role="img"
                        aria-label={`Picture for the question: ${questions[currentQuestion]?.question}`}
                        data-testid="question-visual"
                      >
                        {questions[currentQuestion].visual}
                      </div>
                    )}
                  </div>

                  {/* Answer Section - Multiple Choice, True/False, or Input.
                      Fallback logic:
                      - true_false → auto-inject ["True", "False"] options
                      - multiple_choice with no options → drop to typed input
                      - input/short_answer → typed input (but not for pre-K, they get options-only) */}
                  {(() => {
                    const q = questions[currentQuestion] || {};
                    let opts = Array.isArray(q.options) ? q.options : [];
                    const looksLikeTrueFalse = q.type === 'true_false' ||
                      /^\s*true\s*(or|\/)\s*false[:\s]/i.test(q.question || '');
                    // Auto-inject T/F choices when the question is a T/F but options are missing.
                    if (looksLikeTrueFalse && opts.length === 0) {
                      opts = ['True', 'False'];
                      q.options = opts;
                    }
                    const looksLikeInput = q.type === 'input' || q.type === 'short_answer' || q.type === 'fill_in_blank';
                    const hasOptions = opts.length > 0;
                    const showInput = (!preK && looksLikeInput) || (!hasOptions && !looksLikeInput);
                    return showInput;
                  })() ? (
                    /* Text Input Answer */
                    <div className="space-y-3">
                      <Input
                        value={typedAnswer}
                        onChange={(e) => setTypedAnswer(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && !feedback && submitAnswer()}
                        placeholder="Type your answer here..."
                        disabled={feedback !== null}
                        className={`h-14 text-lg ${
                          feedback 
                            ? feedback.correct 
                              ? 'border-emerald-500 bg-emerald-50' 
                              : 'border-red-500 bg-red-50'
                            : ''
                        }`}
                        data-testid="typed-answer-input"
                        autoFocus
                      />
                      {feedback && feedback.correct_answer && (
                        <p className="text-sm text-slate-600">
                          Correct answer: <span className="font-semibold text-emerald-600">{feedback.correct_answer}</span>
                        </p>
                      )}
                    </div>
                  ) : (
                    /* Multiple Choice Options */
                    <div className="space-y-3">
                      {questions[currentQuestion]?.options?.map((option, idx) => {
                        const isSelected = selectedAnswer === option;
                        const isCorrect = feedback?.correct_answer === option;
                        const showResult = feedback !== null;
                        
                        let optionStyle = 'border-slate-200 hover:border-slate-300 hover:bg-slate-50';
                        if (showResult) {
                          if (isCorrect) {
                            optionStyle = 'border-emerald-500 bg-emerald-50';
                          } else if (isSelected && !feedback?.correct) {
                            optionStyle = 'border-red-500 bg-red-50';
                          }
                        } else if (isSelected) {
                          optionStyle = 'border-blue-500 bg-blue-50';
                        }
                        
                        return (
                          <button
                            key={idx}
                            onClick={() => {
                              if (feedback) return;
                              setSelectedAnswer(option);
                              if (preK) speakText(String(option));
                            }}
                            disabled={feedback !== null}
                            className={`w-full text-left rounded-xl border-2 transition-all ${optionStyle} ${preK ? 'p-6 text-2xl font-bold' : 'p-4'}`}
                            data-testid={`answer-option-${idx}`}
                          >
                            <div className="flex items-center justify-between">
                              <span className="text-slate-700">{option}</span>
                              {showResult && isCorrect && (
                                <CheckCircle className={preK ? "w-8 h-8 text-emerald-500" : "w-5 h-5 text-emerald-500"} />
                              )}
                              {showResult && isSelected && !feedback?.correct && (
                                <XCircle className={preK ? "w-8 h-8 text-red-500" : "w-5 h-5 text-red-500"} />
                              )}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {/* Feedback */}
                  {feedback && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`mt-6 p-4 rounded-xl ${
                        feedback.correct 
                          ? 'bg-emerald-50 border border-emerald-200' 
                          : 'bg-amber-50 border border-amber-200'
                      }`}
                    >
                      <p className={`font-medium ${feedback.correct ? 'text-emerald-700' : 'text-amber-700'}`}>
                        {feedback.encouragement}
                      </p>
                      {feedback.explanation && (
                        <p className="text-slate-600 mt-2 text-sm">{feedback.explanation}</p>
                      )}
                      {levelChange && (
                        <div className={`mt-3 flex items-center gap-2 ${levelChange === 'up' ? 'text-emerald-600' : 'text-amber-600'}`}>
                          {levelChange === 'up' ? (
                            <>
                              <TrendingUp className="w-5 h-5" />
                              <span className="font-medium">Level Up! You're advancing!</span>
                            </>
                          ) : (
                            <>
                              <TrendingDown className="w-5 h-5" />
                              <span className="font-medium">Let's practice more at this level.</span>
                            </>
                          )}
                        </div>
                      )}
                    </motion.div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex gap-3 mt-6">
                    {!feedback ? (
                      <Button
                        onClick={submitAnswer}
                        disabled={
                          questions[currentQuestion]?.type === 'input' || 
                          questions[currentQuestion]?.type === 'short_answer' || 
                          questions[currentQuestion]?.type === 'fill_in_blank'
                            ? !typedAnswer.trim()
                            : !selectedAnswer
                        }
                        className={`w-full bg-gradient-to-r ${config.bgGradient}`}
                        data-testid="submit-answer-btn"
                      >
                        Check Answer
                      </Button>
                    ) : (
                      <Button
                        onClick={nextQuestion}
                        className={`w-full bg-gradient-to-r ${config.bgGradient}`}
                        data-testid="next-question-btn"
                      >
                        {currentQuestion < questions.length - 1 ? (
                          <>
                            Next Question
                            <ArrowRight className="w-4 h-4 ml-2" />
                          </>
                        ) : (
                          <>
                            See Results
                            <Trophy className="w-4 h-4 ml-2" />
                          </>
                        )}
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* RESULTS PHASE */}
          {phase === 'results' && (
            <motion.div
              key="results"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="space-y-6"
            >
              <Card className="border-0 shadow-xl overflow-hidden" data-testid="results-card">
                <div className={`bg-gradient-to-r ${config.bgGradient} p-8 text-center text-white`}>
                  <Trophy className="w-16 h-16 mx-auto mb-4 text-amber-300" />
                  <h2 className="text-3xl font-bold mb-2">
                    {sessionStats?.mastery_score >= 80 ? 'Excellent Work!' : 
                     sessionStats?.mastery_score >= 60 ? 'Good Progress!' : 'Keep Practicing!'}
                  </h2>
                  <p className="text-white/80">You've completed this practice session</p>
                </div>
                
                <CardContent className="p-6 space-y-6">
                  {/* Save this session to your library */}
                  <div className="flex items-center justify-between gap-3 p-4 rounded-xl bg-rose-50 border border-rose-200">
                    <div>
                      <p className="text-sm font-bold text-rose-800">Loved this practice?</p>
                      <p className="text-xs text-rose-600">Save it to your library so you can come back to this skill anytime.</p>
                    </div>
                    <LikeButton
                      childId={childId}
                      contentType="ai_story"
                      contentId={`adaptive-${subject}-${lesson?.skill_focus || 'session'}-${(sessionStats?.session_id || lesson?.id || '').toString().slice(-8) || Date.now()}`}
                      size="lg"
                      metadata={{
                        title: lesson?.title || `${config.name} practice`,
                        subject,
                        skill_focus: lesson?.skill_focus,
                        mastery_score: sessionStats?.mastery_score,
                        grade_range: lesson?.grade_range,
                      }}
                      testIdPrefix="adaptive-results-like"
                    />
                  </div>
                  {/* Stats Grid */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="text-center p-4 bg-slate-50 rounded-xl">
                      <p className="text-3xl font-bold text-slate-800">
                        {sessionStats?.questions_correct || 0}/{sessionStats?.questions_answered || questions.length}
                      </p>
                      <p className="text-sm text-slate-500">Correct Answers</p>
                    </div>
                    <div className="text-center p-4 bg-emerald-50 rounded-xl">
                      <p className="text-3xl font-bold text-emerald-600">
                        {sessionStats?.mastery_score || 0}%
                      </p>
                      <p className="text-sm text-slate-500">Mastery Score</p>
                    </div>
                    <div className="text-center p-4 bg-amber-50 rounded-xl">
                      <p className="text-3xl font-bold text-amber-600">
                        +{(sessionStats?.questions_answered || questions.length) * 5}
                      </p>
                      <p className="text-sm text-slate-500">XP Earned</p>
                    </div>
                    <div className="text-center p-4 bg-blue-50 rounded-xl">
                      <p className="text-3xl font-bold text-blue-600">
                        Level {child?.subject_levels?.[subject] || 1}
                      </p>
                      <p className="text-sm text-slate-500">Current Level</p>
                    </div>
                  </div>

                  {/* Areas Summary */}
                  {(sessionStats?.strong_areas?.length > 0 || sessionStats?.weak_areas?.length > 0) && (
                    <div className="grid md:grid-cols-2 gap-4">
                      {sessionStats?.strong_areas?.length > 0 && (
                        <div className="p-4 bg-emerald-50 rounded-xl border border-emerald-100">
                          <h3 className="font-semibold text-emerald-800 flex items-center gap-2 mb-2">
                            <Star className="w-4 h-4" />
                            Strong Areas
                          </h3>
                          <div className="flex flex-wrap gap-2">
                            {sessionStats.strong_areas.map((area, idx) => (
                              <Badge key={idx} className="bg-emerald-100 text-emerald-700 border-0">
                                {area.replace(/_/g, ' ')}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                      {sessionStats?.weak_areas?.length > 0 && (
                        <div className="p-4 bg-amber-50 rounded-xl border border-amber-100">
                          <h3 className="font-semibold text-amber-800 flex items-center gap-2 mb-2">
                            <Target className="w-4 h-4" />
                            Focus Areas
                          </h3>
                          <div className="flex flex-wrap gap-2">
                            {sessionStats.weak_areas.map((area, idx) => (
                              <Badge key={idx} className="bg-amber-100 text-amber-700 border-0">
                                {area.replace(/_/g, ' ')}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Practice missed questions */}
              {(retryQueue.length > 0 || loadingRetry) && (
                <Card className="border-2 border-amber-300 bg-amber-50/60" data-testid="practice-missed-card">
                  <CardContent className="p-5 flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-amber-500 flex items-center justify-center text-white shrink-0">
                      <Target className="w-6 h-6" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-bold text-amber-900">Practice questions you missed</h3>
                      <p className="text-xs text-amber-700">
                        {loadingRetry
                          ? 'Loading your missed questions…'
                          : `${retryQueue.length} question${retryQueue.length === 1 ? '' : 's'} to try again. Getting a second chance builds mastery.`}
                      </p>
                    </div>
                    <Button
                      onClick={startRetryPractice}
                      disabled={loadingRetry || retryQueue.length === 0}
                      className="bg-amber-500 hover:bg-amber-600 text-white shrink-0"
                      data-testid="practice-missed-btn"
                    >
                      <RefreshCw className="w-4 h-4 mr-1" /> Try again
                    </Button>
                  </CardContent>
                </Card>
              )}

              {/* Action Buttons */}
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => navigate(`/learn/${childId}`)}
                  className="flex-1"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Learning
                </Button>
                <Button
                  onClick={restartLesson}
                  className={`flex-1 bg-gradient-to-r ${config.bgGradient}`}
                  data-testid="continue-learning-btn"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Continue Learning
                </Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </main>
    </div>
  );
};

export default AdaptiveAICurriculum;
